/**
 * React Query hooks for Cube.js analytics (Insights).
 *
 * All queries are executed via POST /v1/insights with the Cube.js query
 * constructed in the frontend. The backend handles security by filtering
 * results based on the user's accessible corporations.
 */

import { QueryClient, useQuery } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";

/**
 * Generic Cube.js query response type
 */
export type CubeQueryResponse<T> = {
  data: T[];
  annotation?: {
    measures: Record<string, unknown>;
    dimensions: Record<string, unknown>;
    segments: Record<string, unknown>;
    timeDimensions: Record<string, unknown>;
  };
};

/**
 * Programme hectares data shape from ProgrammeHectares cube
 */
export type ProgrammeHectaresData = {
  "ProgrammeHectares.programmeGuid": string;
  "ProgrammeHectares.programmeName": string;
  "ProgrammeHectares.totalHectares": number;
  "ProgrammeHectares.farmCount": number;
  "ProgrammeHectares.farmerCount": number;
  "ProgrammeHectares.fieldCount": number;
};

/**
 * Emissions data shape from Emissions cube
 */
export type EmissionsData = {
  "Emissions.parameter": string;
  "Emissions.avgValue": number;
};

/**
 * Bird observations data shape from BirdObservations cube
 */
export type BirdObservationsData = {
  "BirdObservations.distinctBirdCount": number;
};

/**
 * Biodiversity data shape from Biodiversity cube
 */
export type BiodiversityData = {
  "Biodiversity.distinctHabitatCodeCount": number;
  "Biodiversity.totalLengthM": number;
  "Biodiversity.totalAreaHa": number;
  "Biodiversity.totalAreaAcres": number;
  "Biodiversity.count": number;
  "Biodiversity.habitatGroup"?: string;
};

/**
 * Submitted actions data shape from SubmittedActions cube
 */
export type SubmittedActionsData = {
  "SubmittedActions.count": number;
  "SubmittedActions.uniqueUserCount": number;
  "SubmittedActions.uniqueActionCount": number;
  "SubmittedActions.uniqueCategoryCount": number;
};

/**
 * Cube.js query structure
 */
export type CubeQuery = {
  measures?: string[];
  dimensions?: string[];
  filters?: Array<{
    member: string;
    operator: string;
    values: string[];
  }>;
  timeDimensions?: Array<{
    dimension: string;
    granularity?: string;
    dateRange?: string | string[];
  }>;
  order?: Record<string, "asc" | "desc">;
  limit?: number;
  offset?: number;
};

class InsightsApi {
  baseUrl: string;

  constructor() {
    this.baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/v1/insights`;
  }

  /**
   * Execute a Cube.js query
   */
  async query<T = unknown>(
    cubeQuery: CubeQuery
  ): Promise<CubeQueryResponse<T>> {
    const res = await fetch(this.baseUrl, {
      mode: "cors",
      method: "POST",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query: cubeQuery }),
    });

    if (!res.ok) {
      throw new Error(`Failed to execute insights query: ${res.status}`);
    }

    return (await res.json()) as CubeQueryResponse<T>;
  }

  /**
   * Get Cube.js metadata (available cubes, measures, dimensions)
   */
  async getMeta(): Promise<unknown> {
    const res = await fetch(`${this.baseUrl}/meta`, {
      mode: "cors",
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch insights metadata: ${res.status}`);
    }

    return res.json();
  }

  async getFarmerImage(farmerGuid?: string): Promise<{ url: string | null }> {
    const params = farmerGuid
      ? `?farmerGuid=${encodeURIComponent(farmerGuid)}`
      : "";
    const res = await fetch(`${this.baseUrl}/farmer-image${params}`, {
      mode: "cors",
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch farmer image: ${res.status}`);
    }

    return (await res.json()) as { url: string | null };
  }
}

const insightsApi = new InsightsApi();

/**
 * Execute a Cube.js query.
 *
 * @param cubeQuery - The Cube.js query object
 * @param queryKey - Unique key for React Query caching
 * @param enabled - Whether to run the query
 */
export function useInsightsQuery<T = unknown>(
  cubeQuery: CubeQuery,
  queryKey: string,
  enabled = true
) {
  return useQuery({
    queryKey: [...queryKeys.insights.all, queryKey],
    queryFn: async () => {
      return insightsApi.query<T>(cubeQuery);
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch Cube.js metadata
 */
export function useInsightsMeta(enabled = true) {
  return useQuery({
    queryKey: queryKeys.insights.meta,
    queryFn: async () => {
      return insightsApi.getMeta();
    },
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes - metadata rarely changes
  });
}

export { insightsApi };

/**
 * Fetch the farmer's biodiversity image (signed S3 URL)
 */
export function useFarmerImage(farmerGuid?: string) {
  return useQuery({
    queryKey: [...queryKeys.insights.all, "farmerImage", farmerGuid ?? "self"],
    queryFn: () => insightsApi.getFarmerImage(farmerGuid),
    staleTime: 30 * 60 * 1000, // 30 minutes - image rarely changes
  });
}

/**
 * Poll insights queries for fresh data after a mutation.
 *
 * Cube.js caches query results and checks its refreshKey every ~2 seconds.
 * After a mutation that affects insights data, we invalidate matching queries
 * immediately and then re-invalidate every 2s for 6s to catch the backend
 * cache refresh.
 *
 * @param queryClient - React Query client
 * @param queryKeyPrefixes - Prefixes to match against insights query keys
 *   (e.g. ["feedInStore_", "yieldSummary_"])
 */
export function pollInsightsRefresh(
  queryClient: QueryClient,
  queryKeyPrefixes: string[]
) {
  const invalidate = () => {
    void queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        if (key[0] !== "insights") return false;
        const subKey = key[1] as string | undefined;
        return queryKeyPrefixes.some((prefix) => subKey?.startsWith(prefix));
      },
    });
  };

  invalidate();

  let count = 0;
  const interval = setInterval(() => {
    count++;
    invalidate();
    if (count >= 3) {
      clearInterval(interval);
    }
  }, 2000);
}
